import { useAddRoutingLocationRulesForm } from "../hooks/use-add-routing-location-form";
import { Form, FormField } from "@babackapp/ui/components/form";
import { useCarriersContext } from "../../use-carriers-context";
import { Input } from "@babackapp/ui/components/input";
import { getCurrentLocale } from "@/lib/translator";
import { getReasonTextFromIdAndLang } from "@/lib/reasons/reason-text-from-id-and-lang";
import { Button } from "@babackapp/ui/components/button";
import useAddOneShopRoutingLocationRules from "@babackapp/private-api-client/client/use-add-one-shop-routing-location-rules";
import useTranslation from "@babackapp/translations/use-translation";
import FormOptionsSelector from "@babackapp/react-hook-forms-components/form-option-selector";
import { ShopRoutingLocationRuleDto } from "@babackapp/private-api-client/types";
import { useCallback } from "react";

interface AddOrEditRoutingLocationRuleProps {
  routingLocationRule?: any;
  onCloseForm?: () => void;
}

export function AddRoutingLocationRule({
  routingLocationRule: initialRoutingLocationRule,
  onCloseForm,
}: AddOrEditRoutingLocationRuleProps) {
  const t = useTranslation();
  const { shop } = useCarriersContext();

  const { mutate: addRoutingLocationRules } = useAddOneShopRoutingLocationRules(
    shop.id,
  );

  const onSubmit = useCallback(
    async (data: ShopRoutingLocationRuleDto) => {
      addRoutingLocationRules(data);
      onCloseForm && onCloseForm();
    },
    [addRoutingLocationRules, onCloseForm],
  );

  const { handleSubmit, form, control, values, setValue } =
    useAddRoutingLocationRulesForm({ onSubmit });

  return (
    <Form {...form}>
      <form>
        <div className="space-y-2">
          <div className="text">
            {t("settings.shipping.rules.ifTheReturnRequest")}
          </div>
          <div className="flex space-x-2">
            <div className="flex-1">
              <FormField
                control={control}
                name="operator"
                render={({ field }) => (
                  <FormOptionsSelector
                    label={""}
                    options={[
                      {
                        key: "equals",
                        label: t("settings.shipping.rules.equals"),
                      },
                    ]}
                    {...field}
                  />
                )}
              />
            </div>
            <div className="flex-1">
              <FormField
                control={control}
                name="key"
                render={({ field }) => (
                  <FormOptionsSelector
                    label={""}
                    options={[
                      {
                        key: "reasonId",
                        label: t("settings.shipping.rules.reason"),
                      },
                      {
                        key: "countryCode",
                        label: t("settings.shipping.rules.countryCode"),
                      },
                      {
                        key: "orderTag",
                        label: t("settings.shipping.rules.orderTag"),
                      },
                      {
                        key: "fulfillmentOriginCountryCode",
                        label: t(
                          "settings.shipping.rules.fulfillmentOriginCountryCode",
                        ),
                      },
                    ]}
                    {...field}
                  />
                )}
              />
            </div>
          </div>
          {values.key === "countryCode" && (
            <Input
              type="text"
              value={values.value}
              onChange={(e) => setValue("value", e.target.value.toUpperCase())}
              className="input-class"
            />
          )}
          {values.key === "orderTag" && (
            <Input
              type="text"
              value={values.value}
              onChange={(e) => setValue("value", e.target.value.toUpperCase())}
              className="input-class"
            />
          )}
          {values.key === "fulfillmentOriginCountryCode" && (
            <Input
              type="text"
              value={values.value}
              onChange={(e) => setValue("value", e.target.value.toUpperCase())}
              className="input-class"
            />
          )}
          {values.key === "reasonId" && (
            <>
              <FormField
                control={control}
                name="value"
                render={({ field }) => (
                  <FormOptionsSelector
                    label={""}
                    options={shop.reasons
                      .filter((reason) => reason.active)
                      .map((r) => {
                        return {
                          key: r.id,
                          label:
                            getReasonTextFromIdAndLang(
                              r.id || "",
                              getCurrentLocale()?.code || "",
                              shop,
                            ) || "",
                        } as any;
                      })}
                    {...field}
                  />
                )}
              />
            </>
          )}
          <div className="text">{t("settings.shipping.rules.sendItTo")}</div>

          <FormField
            control={control}
            name="positiveResult"
            render={({ field }) => (
              <FormOptionsSelector
                label={""}
                options={shop.locations.map((location) => ({
                  key: location.id || "",
                  label:
                    location?.address?.companyName ||
                    location?.address?.address1 ||
                    "",
                }))}
                {...field}
              />
            )}
          />
        </div>
        <div className="flex justify-between mt-5">
          <Button type="button" variant="outline" onClick={onCloseForm}>
            {t("misc.cancel")}
          </Button>
          <Button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
          >
            {initialRoutingLocationRule
              ? t("misc.update")
              : t("settings.shipping.rules.addRule")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
